import React, { useState, useRef, useCallback } from "react";
import "./App.css";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Stack,
  Grid,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  UploadFile as UploadFileIcon,
  TextFields as TextIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import * as pdfjsLib from "pdfjs-dist";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import ControlsBar from "./components/ControlsBar";
import SEOHead from "./components/SEOHead";

// Use CDN worker to avoid copy-file setup
(pdfjsLib as any).GlobalWorkerOptions.workerSrc =
  `//unpkg.com/pdfjs-dist@${(pdfjsLib as any).version}/build/pdf.worker.min.js`;

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

type InputType = "text" | "pdf";
type SummaryLength = "short" | "medium" | "detailed";

// ─── PDF text extraction ────────────────────────────────────────────────────
async function extractPDFText(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await (pdfjsLib as any).getDocument({ data: arrayBuffer }).promise;
  let text = "";
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map((item: any) => item.str).join(" ") + "\n";
  }
  return text.trim();
}

// ─── Inner app (has access to context) ───────────────────────────────────────
const SummarizeApp: React.FC = () => {
  const { t, isRTL, language } = useLanguage();
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === "dark";

  const [inputType, setInputType] = useState<InputType>("text");
  const [textContent, setTextContent] = useState("");
  const [summaryLength, setSummaryLength] = useState<SummaryLength>("medium");

  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfText, setPdfText] = useState("");
  const [pdfExtracting, setPdfExtracting] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const [pdfDragOver, setPdfDragOver] = useState(false);

  const PDF_CHAR_LIMIT = 12000;

  const pdfInputRef = useRef<HTMLInputElement>(null);

  // ── PDF upload handling ────────────────────────────────────────────────────
  const handlePDFFile = useCallback(async (file: File) => {
    if (!file.type.includes("pdf")) return;
    setPdfFile(file);
    setPdfError("");
    setPdfExtracting(true);
    try {
      const text = await extractPDFText(file);
      if (text.length < 50) {
        setPdfError(t("errors.pdfTooShort"));
        setPdfText("");
      } else {
        setPdfText(text);
      }
    } catch {
      setPdfError(t("errors.failed"));
      setPdfText("");
    } finally {
      setPdfExtracting(false);
    }
  }, [t]);

  // ── Drag & drop (PDF only) ─────────────────────────────────────────────────
  const onPdfDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setPdfDragOver(false);
    if (e.dataTransfer.files[0]) handlePDFFile(e.dataTransfer.files[0]);
  }, [handlePDFFile]);

  // ── Summarize ──────────────────────────────────────────────────────────────
  const handleSummarize = async () => {
    setError("");
    setSummary("");

    const content = inputType === "text" ? textContent : pdfText;
    if (!content.trim()) { setError(t("errors.noContent")); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/summarize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "text", content, length: summaryLength, language }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSummary(data.summary || "");
    } catch {
      setError(t("errors.failed"));
    } finally {
      setLoading(false);
    }
  };

  // ── Copy ───────────────────────────────────────────────────────────────────
  const handleCopy = () => {
    navigator.clipboard.writeText(summary).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Reset ──────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setSummary("");
    setError("");
    setTextContent("");
    setPdfFile(null); setPdfText(""); setPdfError("");
  };

  // ── Length options ──────────────────────────────────────────────────────────
  const lengths: { value: SummaryLength; label: string; desc: string }[] = [
    { value: "short",    label: t("length.short"),    desc: t("length.shortDesc") },
    { value: "medium",   label: t("length.medium"),   desc: t("length.mediumDesc") },
    { value: "detailed", label: t("length.detailed"), desc: t("length.detailedDesc") },
  ];

  const inputTypes: { value: InputType; label: string; icon: React.ReactNode }[] = [
    { value: "text", label: t("input.typeText"), icon: <TextIcon fontSize="small" /> },
    { value: "pdf",  label: t("input.typePDF"),  icon: <PdfIcon fontSize="small" /> },
  ];

  return (
    <>
      <SEOHead language={language} />
      <ControlsBar />

      {/* Ambient background */}
      <div className="ambient-stage" aria-hidden="true">
        <div className="orb orb-one" />
        <div className="orb orb-two" />
        <div className="orb orb-three" />
      </div>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ pt: { xs: "5.75rem", md: "5rem" }, pb: 0 }}>
        <Paper className="hero-panel" elevation={0}>
          <Grid container spacing={4} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography variant="h2" component="h1" className="hero-title">
                {t("hero.title")}
              </Typography>
              <Typography
                variant="h6"
                className="hero-subtitle"
                sx={{
                  maxWidth: 520,
                  mt: 1.5,
                  color: isDark ? "#d6c2ae" : "#71665d",
                  fontWeight: 450,
                  lineHeight: 1.55,
                }}
              >
                {t("hero.subtitle")}
              </Typography>
              <Stack className="hero-feature-chips" direction="row" sx={{ mt: 3, flexWrap: "wrap", gap: 1.5 }}>
                <Chip className="hero-feature-chip" icon={<TextIcon />} label={t("hero.chipText")} />
                <Chip className="hero-feature-chip" icon={<PdfIcon />}  label={t("hero.chipPDF")} />
                <Chip className="hero-feature-chip" label={t("hero.chipFree")} />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box className="hero-simple-card">
                <Typography
                  variant="overline"
                  sx={isRTL ? { letterSpacing: "normal", textTransform: "none" } : undefined}
                >
                  {t("hero.howItWorks")}
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                  {[
                    t("hero.step1"),
                    t("hero.step2"),
                    t("hero.step3"),
                  ].map((step, i) => (
                    <Stack key={i} className="hero-step-row" direction="row" sx={{ alignItems: "flex-start" }}>
                      <Box className="step-number">{i + 1}</Box>
                      <Typography className="hero-step-text" variant="body2">{step}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Container maxWidth="md" sx={{ pt: { xs: 4, md: 5 }, pb: 8, minHeight: "60vh" }}>

        {/* ── Input card ──────────────────────────────────────────────────── */}
        {!summary && (
          <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 3 }}>

            {/* Input type selector */}
            <Stack direction="row" useFlexGap spacing={1} sx={{ mb: 3 }}>
              {inputTypes.map(({ value, label, icon }) => (
                <Button
                  key={value}
                  variant={inputType === value ? "contained" : "outlined"}
                  size="small"
                  startIcon={icon}
                  onClick={() => { setInputType(value); setError(""); }}
                  sx={{
                    borderRadius: "999px !important",
                    px: 2,
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    ...(inputType === value ? {} : {
                      borderColor: "divider",
                      color: "text.secondary",
                    }),
                  }}
                >
                  {label}
                </Button>
              ))}
            </Stack>

            {/* Text input */}
            {inputType === "text" && (
              <TextField
                multiline
                rows={8}
                fullWidth
                placeholder={t("input.textPlaceholder")}
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                sx={{ mb: 3 }}
                slotProps={{ htmlInput: { dir: "auto" } }}
              />
            )}

            {/* PDF upload */}
            {inputType === "pdf" && (
              <Box
                onDragOver={(e) => { e.preventDefault(); setPdfDragOver(true); }}
                onDragLeave={() => setPdfDragOver(false)}
                onDrop={onPdfDrop}
                onClick={() => !pdfFile && pdfInputRef.current?.click()}
                sx={{
                  mb: 3,
                  border: "2px dashed",
                  borderColor: pdfDragOver ? "primary.main" : pdfFile ? "success.main" : "divider",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  cursor: pdfFile ? "default" : "pointer",
                  backgroundColor: pdfDragOver ? "action.hover" : "transparent",
                  transition: "all 0.2s",
                }}
              >
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf"
                  style={{ display: "none" }}
                  onChange={(e) => e.target.files?.[0] && handlePDFFile(e.target.files[0])}
                />
                {pdfExtracting ? (
                  <Stack sx={{ alignItems: "center" }} spacing={1.5}>
                    <CircularProgress size={28} color="primary" />
                    <Typography variant="body2" color="text.secondary">{t("input.extracting")}</Typography>
                  </Stack>
                ) : pdfFile && pdfText ? (
                  <Stack sx={{ alignItems: "center" }} spacing={1}>
                    <PdfIcon sx={{ fontSize: 36, color: "success.main" }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }} color="success.main">{t("input.pdfReady")}</Typography>
                    <Typography variant="caption" color="text.secondary">{pdfFile.name}</Typography>
                    {pdfText.length > PDF_CHAR_LIMIT && (
                      <Typography variant="caption" sx={{ color: "warning.main", textAlign: "center", maxWidth: 280 }}>
                        {t("input.pdfTruncated")}
                      </Typography>
                    )}
                    <Button size="small" variant="text" onClick={(e) => { e.stopPropagation(); setPdfFile(null); setPdfText(""); pdfInputRef.current && (pdfInputRef.current.value = ""); }}>
                      {t("input.changeFile")}
                    </Button>
                  </Stack>
                ) : (
                  <Stack sx={{ alignItems: "center" }} spacing={1}>
                    <UploadFileIcon sx={{ fontSize: 36, color: "text.disabled" }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{t("input.uploadPDF")}</Typography>
                    <Typography variant="caption" color="text.secondary">{t("input.uploadPDFSub")}</Typography>
                  </Stack>
                )}
                {pdfError && <Alert severity="error" sx={{ mt: 2, textAlign: isRTL ? "right" : "left" }}>{pdfError}</Alert>}
              </Box>
            )}

            {/* Summary length */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: "block", textTransform: isRTL ? "none" : "uppercase", letterSpacing: isRTL ? "normal" : "0.08em" }}>
                {t("length.label")}
              </Typography>
              <Stack direction="row" useFlexGap spacing={1}>
                {lengths.map(({ value, label, desc }) => (
                  <Tooltip key={value} title={desc} arrow>
                    <Chip
                      label={label}
                      onClick={() => setSummaryLength(value)}
                      variant={summaryLength === value ? "filled" : "outlined"}
                      color={summaryLength === value ? "primary" : "default"}
                      sx={{
                        fontWeight: 600,
                        cursor: "pointer",
                        borderRadius: "999px",
                        ...(summaryLength !== value ? { borderColor: "divider", color: "text.secondary" } : {}),
                      }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Box>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Summarize button */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={handleSummarize}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={{ py: 1.5, fontWeight: 700, fontSize: "1rem", borderRadius: 2 }}
            >
              {loading ? t("button.summarizing") : t("button.summarize")}
            </Button>
          </Paper>
        )}

        {/* ── Output card ─────────────────────────────────────────────────── */}
        {summary && (
          <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 3 }}>
            <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t("output.title")}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title={copied ? t("output.copied") : t("output.copy")}>
                  <IconButton size="small" onClick={handleCopy} color={copied ? "success" : "default"}>
                    {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("output.summarizeAnother")}>
                  <IconButton size="small" onClick={handleReset}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.85,
                color: "text.secondary",
                whiteSpace: "pre-wrap",
                direction: isRTL ? "rtl" : "ltr",
              }}
            >
              {summary}
            </Typography>

            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              sx={{ mt: 3, borderRadius: "999px", fontWeight: 600, color: "text.secondary", borderColor: "divider" }}
            >
              {t("output.summarizeAnother")}
            </Button>
          </Paper>
        )}

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <Box sx={{ mt: 6, textAlign: "center" }}>
          <Typography variant="caption" color="text.disabled">
            © {new Date().getFullYear()} my-aitools.online
            {" · "}
            <Box
              component="a"
              href="/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "text.disabled", textDecoration: "none", "&:hover": { color: "primary.main" } }}
            >
              {t("footer.privacy")}
            </Box>
          </Typography>
        </Box>

      </Container>
    </>
  );
};

// ─── Root with providers ─────────────────────────────────────────────────────
const App: React.FC = () => (
  <ThemeProvider>
    <LanguageProvider>
      <SummarizeApp />
    </LanguageProvider>
  </ThemeProvider>
);

export default App;
